import {Alert, Spin} from 'antd'
import {getType} from 'apis/model/base'
import ApiInfoForm from 'components/form/ApiInfoForm'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import Pure from 'components/Pure'
import {FormikConsumer} from 'formik'
import _ from 'lodash'
import React, {useMemo} from 'react'
import {compose} from 'recompose'
import {fromObject, NullAsDefault, renderElse, renderOwnChild, renderSelf, shallowDiff,} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import createInitialValues from './createInitialValues'
import useTranslate from "../../../modules/local/useTranslate";
import ForDevelop from "../../../envs/ForDevelop";

const EditableForm = ({
                          item,
                          fields,
                          children,
                          formSchema,
                          Wrapper = 'div',
                          staticValues,
                          Header = NullAsDefault,
                          onPreSubmit = renderSelf,
                          initialized = renderSelf,
                          ActionBar = FormActionBar,
                          useDefaultActionBar = true,
                          ...props
                      }) => {
    const initialValues = useMemo(() => {
        const values = initialized(fromObject(item, fields)) || {}
        return {
            ...createInitialValues(getType(item), CRUD.UPDATE),
            ...values,
        }
    }, [item, fields, initialized])

    return (
        <ApiInfoForm
            onPreSubmit={(values) => ({
                ...onPreSubmit(shallowDiff(values, initialValues), values),
                ...(staticValues || {}),
            })}
            {...props}
            {...{initialValues}}>
            {({isSubmitting = false}) => (
                <Spin spinning={!!isSubmitting}>
                    <Wrapper className="space-y-3 flex flex-col">
                        <Header/>
                        {children}
                        <FieldsFactory
                            formSchema={formSchema}
                            Wrapper={renderOwnChild}
                        />
                        {useDefaultActionBar && <ActionBar className="background"/>}
                    </Wrapper>
                </Spin>
            )}
        </ApiInfoForm>
    )
}

// Map field-name (formik key, thường snake_case) -> i18n key của nhãn hiển thị cho người dùng.
// Formik báo lỗi theo tên field kỹ thuật (from_date, to_time...) - nếu không map, ErrorSummaries
// hiện thẳng chuỗi "from_date" vì không có key i18n trùng tên. Bảng này gom các field dùng lại
// nhiều nơi; form nào có field riêng thì truyền thêm qua prop `labels`.
export const COMMON_FIELD_LABEL_KEYS = {
    from_date: 'from date',
    to_date: 'to date',
    from_time: 'start time',
    to_time: 'end time',
    start_time: 'start time',
    end_time: 'end time',
    phase_title: 'phase title',
    days_of_week: 'day of week',
    location_address: 'locations',
}

export const ErrorSummaries = ({labels = {}} = {}) => {
    const t = useTranslate()
    const resolveLabelKey = (name) => labels[name] || COMMON_FIELD_LABEL_KEYS[name] || name
    return (
        <ForDevelop>
            <Pure>
                <FormikConsumer>
                    {({errors}) =>
                        renderElse(
                            _.isEmpty(errors),
                            <Alert
                                type="warning"
                                message={Object.keys(errors || {}).map((name, index) => {
                                    const value = _.get(errors, name)
                                    const {title, description} = _.isString(value)
                                        ? {
                                            title: resolveLabelKey(name),
                                            description: value,
                                        }
                                        : value || {}
                                    return (
                                        <div key={index} className="space-x-1">
                                        <span className="font-semibold text-orange-500">
                                            {`${t(title)} : `}
                                        </span>
                                            <span className="text-color-200 italic">
                                            {description}
                                        </span>
                                        </div>
                                    )
                                })}
                            />
                        )
                    }
                </FormikConsumer>
            </Pure>
        </ForDevelop>
    )
}

const createEditableForm = (fields, formSchema) =>
    compose((props) => (
        <EditableForm
            {...props}
            {...{fields, formSchema}}
        />
    ))

export default createEditableForm
